import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Mail, Phone, Building2, Calendar, Clock, Shield, Key, Wifi, WifiOff,
  CreditCard, ArrowUpCircle, ArrowDownCircle, Package, BadgeDollarSign,
  Globe, MonitorSmartphone, ShieldAlert, History, BarChart3, RefreshCw,
  ChevronDown, Check, X, AlertTriangle, Wallet, TrendingUp, TrendingDown,
  Activity, Search, Loader2, Copy, ExternalLink, Bell, BellOff, Eye, EyeOff,
  KeyRound, Plus, Trash2, Edit3, Fingerprint, FileText, MoreHorizontal,
  ArrowRight, ChevronRight, ChevronLeft, MessageSquare, Zap, Users as UsersIcon
} from 'lucide-react';

const API_BASE = '/api/v1/admin';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

// ─── Format Helpers ──────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (v, currency = 'INR') => {
  if (v == null) return '—';
  return `${currency === 'INR' ? '₹' : '$'}${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtRate = (v, currency = 'INR') => {
  if (v == null) return '—';
  const num = Number(v);
  const str = num.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  const decimals = str.includes('.') ? str.split('.')[1].length : 0;
  const minDec = Math.max(decimals, 4);
  return `${currency === 'INR' ? '₹' : '$'}${num.toLocaleString('en-IN', { minimumFractionDigits: minDec, maximumFractionDigits: 6 })}`;
};

const fmtNumber = (v) => {
  if (v == null) return '0';
  return Number(v).toLocaleString('en-IN');
};

const StatusBadge = ({ active, trueLabel = 'Active', falseLabel = 'Inactive' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
    active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
    {active ? trueLabel : falseLabel}
  </span>
);

// ─── Modal Components ────────────────────────────────────────────────────────

const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const RateModal = ({ show, onClose, onSubmit, rates = [], currency = 'INR' }) => {
  const [routeRates, setRouteRates] = useState({});
  useEffect(() => {
    const initial = {};
    rates.forEach(r => { initial[r.route_type] = r.price_per_sms || ''; });
    setRouteRates(initial);
  }, [rates]);
  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = Object.entries(routeRates)
      .filter(([_, v]) => v !== '')
      .map(([route, price]) => ({ route_type: route, price_per_sms: parseFloat(price) }));
    onSubmit(payload);
  };
  return (
    <Modal show={show} onClose={onClose} title="Assign Rate">
      <form onSubmit={handleSubmit} className="space-y-4">
        {['TRANSACTIONAL', 'PROMOTIONAL', 'OTP', 'INTERNATIONAL'].map(route => (
          <div key={route}>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{route}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currency === 'INR' ? '₹' : currency}</span>
              <input
                type="number"
                step="0.001"
                value={routeRates[route] || ''}
                onChange={e => setRouteRates({ ...routeRates, [route]: e.target.value })}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                placeholder="0.00"
              />
            </div>
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 transition-colors">
            Assign Rate
          </button>
        </div>
      </form>
    </Modal>
  );
};

const BalanceModal = ({ show, onClose, onSubmit, currency = 'INR' }) => {
  const [type, setType] = useState('credit');
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ transaction_type: type, amount: parseFloat(amount), remarks: remark });
    setType('credit'); setAmount(''); setRemark('');
  };
  return (
    <Modal show={show} onClose={onClose} title="Credit/Debit Balance">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Type</label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setType('credit')} className={`py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
              type === 'credit' ? 'bg-emerald-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}>
              Credit
            </button>
            <button type="button" onClick={() => setType('debit')} className={`py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
              type === 'debit' ? 'bg-red-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}>
              Debit
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currency === 'INR' ? '₹' : currency}</span>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
              placeholder="0.00"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Remark</label>
          <textarea
            value={remark}
            onChange={e => setRemark(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 resize-none"
            placeholder="Optional remark..."
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 transition-colors">
            {type === 'credit' ? 'Credit' : 'Debit'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const UserCRMNew = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [crmData, setCrmData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const searchInputRef = useRef(null);
  const crmDropdownRef = useRef(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [plans, setPlans] = useState([]);

  // Block Keywords/Mobiles management state
  const [kwSearch, setKwSearch] = useState('');
  const [mbSearch, setMbSearch] = useState('');
  const [showAddKwModal, setShowAddKwModal] = useState(false);
  const [showAddMbModal, setShowAddMbModal] = useState(false);
  const [editKw, setEditKw] = useState(null);
  const [editMb, setEditMb] = useState(null);
  const [newKwText, setNewKwText] = useState('');
  const [newMbText, setNewMbText] = useState('');
  const [newMbReason, setNewMbReason] = useState('');

  // Tab state for sections
  const [activeTab, setActiveTab] = useState('overview');

  // F4 keyboard shortcut to focus search box
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F4') {
        e.preventDefault();
        setDropdownOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Load user list
  useEffect(() => {
    setUsersLoading(true);
    fetch(`${API_BASE}/user-crm/list`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => { setUsers(Array.isArray(data) ? data : []); setUsersLoading(false); })
      .catch(() => setUsersLoading(false));
  }, []);

  // Load CRM data when user selected
  const loadCRM = useCallback((userId) => {
    if (!userId) return;
    setLoading(true);
    setCrmData(null);
    fetch(`${API_BASE}/user-crm/${userId}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => { setCrmData(data); setLoading(false); })
      .catch(() => { setLoading(false); showToast('Failed to load user data', 'error'); });
  }, []);

  const selectUser = (userId) => {
    setSelectedUserId(userId);
    setDropdownOpen(false);
    setSearchTerm('');
    loadCRM(userId);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Filter users for dropdown
  const filteredUsers = users.filter(u =>
    !searchTerm || u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.full_name && u.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Reset highlighted index when search changes
  useEffect(() => { setHighlightedIdx(-1); }, [searchTerm]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIdx >= 0 && crmDropdownRef.current) {
      const items = crmDropdownRef.current.querySelectorAll('[data-crm-user]');
      if (items[highlightedIdx]) items[highlightedIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIdx]);

  // Load plans when modal opens
  useEffect(() => {
    if (showPlanModal) {
      const fetchPlans = async () => {
        try {
          const res = await fetch(`${API_BASE}/plans`, { headers: getHeaders() });
          const data = await res.json();
          if (res.ok) setPlans(data.data || []);
        } catch (err) {
          console.error('Failed to load plans:', err);
        }
      };
      fetchPlans();
    }
  }, [showPlanModal]);

  // Keyboard navigation for user dropdown
  const handleCrmSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.min(prev + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0 && highlightedIdx < filteredUsers.length) {
      e.preventDefault();
      selectUser(filteredUsers[highlightedIdx].id);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);
  const p = crmData?.profile;
  const plan = crmData?.plan;
  const rates = crmData?.rates;
  const bal = crmData?.balance;
  const recharges = crmData?.recent_recharges;
  const alerts = crmData?.login_alerts;
  const ipHistory = crmData?.ip_history;
  const smsUsage = crmData?.sms_usage;
  const availablePlans = crmData?.available_plans;
  const entities = crmData?.entities;
  const ipWhitelist = crmData?.ip_whitelist;
  const blockKeywords = crmData?.block_keywords;
  const blockMobiles = crmData?.block_mobiles;

  // ─── Action Handlers ────────────────────────────────────────────────────────

  const handleAssignPlan = async (planId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/assign-plan`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ plan_id: planId }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Plan "${data.plan_name}" assigned successfully`);
        setShowPlanModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed to assign plan', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleAssignRate = async (pricePerSms, routeType, currency) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/assign-rate`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ price_per_sms: parseFloat(pricePerSms), route_type: routeType, currency }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Rate assigned successfully');
        setShowRateModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed to assign rate', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleCreditDebit = async (type, amount, reason) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/balance`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ type, amount: parseFloat(amount), reason }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`${type === 'credit' ? 'Credited' : 'Debited'} ${fmtMoney(amount)} successfully`);
        setShowBalanceModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  // ─── Block Keywords/Mobiles CRUD ────────────────────────────────────────────

  const handleAddKeyword = async () => {
    if (!newKwText.trim() || !selectedUserId) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ keywords: newKwText, scope: 'USER', user_id: selectedUserId, is_active: true }),
      });
      if (res.ok) {
        showToast('Keyword(s) added');
        setShowAddKwModal(false); setNewKwText('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleUpdateKeyword = async () => {
    if (!editKw || !newKwText.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block/${editKw.id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ keyword: newKwText, scope: editKw.scope, user_id: editKw.scope === 'USER' ? selectedUserId : null, is_active: editKw.is_active }),
      });
      if (res.ok) {
        showToast('Keyword updated');
        setEditKw(null); setNewKwText('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleDeleteKeyword = async (id) => {
    if (!window.confirm('Deactivate this keyword?')) return;
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { showToast('Keyword deactivated'); loadCRM(selectedUserId); }
      else showToast('Failed', 'error');
    } catch { showToast('Network error', 'error'); }
  };

  const handleAddMobile = async () => {
    if (!newMbText.trim() || !selectedUserId) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ mobile_numbers: newMbText, scope: 'USER', user_id: selectedUserId, reason: newMbReason, is_active: true }),
      });
      if (res.ok) {
        showToast('Mobile(s) blocked');
        setShowAddMbModal(false); setNewMbText(''); setNewMbReason('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleUpdateMobile = async () => {
    if (!editMb || !newMbText.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block/${editMb.id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ mobile_number: newMbText, scope: editMb.scope, user_id: editMb.scope === 'USER' ? selectedUserId : null, reason: newMbReason, is_active: editMb.is_active }),
      });
      if (res.ok) {
        showToast('Mobile updated');
        setEditMb(null); setNewMbText(''); setNewMbReason('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleDeleteMobile = async (id) => {
    if (!window.confirm('Deactivate this blocked mobile?')) return;
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { showToast('Mobile unblocked'); loadCRM(selectedUserId); }
      else showToast('Failed', 'error');
    } catch { showToast('Network error', 'error'); }
  };

  // ─── Greeting ──────────────────────────────────────────────────────────────
  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const todayStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // ─── SMS Usage Chart (area-style) ──────────────────────────────────────────
  const SMSUsageChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-sm text-gray-400 text-center py-10">No SMS usage data available</p>;
    const reversed = [...data].reverse();
    const maxCount = Math.max(...reversed.map(d => d.sms_count || 0), 1);

    return (
      <div className="relative">
        {/* Y-axis labels */}
        <div className="flex h-52">
          <div className="flex flex-col justify-between text-[10px] text-gray-400 pr-3 py-1">
            <span>{fmtNumber(maxCount)}</span>
            <span>{fmtNumber(Math.round(maxCount * 0.75))}</span>
            <span>{fmtNumber(Math.round(maxCount * 0.5))}</span>
            <span>{fmtNumber(Math.round(maxCount * 0.25))}</span>
            <span>0</span>
          </div>
          {/* Bars */}
          <div className="flex-1 flex items-end gap-2">
            {reversed.map((m, i) => {
              const total = m.sms_count || 0;
              const delivered = m.delivered || 0;
              const failed = m.failed || 0;
              const pct = (total / maxCount) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  {/* Tooltip */}
                  <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg">
                    <div className="font-semibold mb-0.5">{m.month}</div>
                    <div>Total: {fmtNumber(total)} | Del: {fmtNumber(delivered)} | Fail: {fmtNumber(failed)}</div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                  {/* Bar */}
                  <div className="w-full rounded-t-lg overflow-hidden transition-all duration-300 group-hover:opacity-80" style={{ height: `${Math.max(pct, 2)}%` }}>
                    <div className="w-full h-full relative" style={{ background: 'linear-gradient(180deg, #6366f1 0%, #818cf8 50%, #a5b4fc 100%)' }}>
                      {failed > 0 && total > 0 && (
                        <div className="absolute bottom-0 w-full bg-red-400/60" style={{ height: `${(failed / total) * 100}%` }} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* X-axis labels */}
        <div className="flex ml-10 mt-2">
          {reversed.map((m, i) => (
            <div key={i} className="flex-1 text-center">
              <span className="text-[10px] text-gray-500 font-medium">{m.month?.substring(0, 3)}</span>
            </div>
          ))}
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-5 mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ background: '#6366f1' }} /><span className="text-[11px] text-gray-500 font-medium">Delivered</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-400" /><span className="text-[11px] text-gray-500 font-medium">Failed</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-amber-300" /><span className="text-[11px] text-gray-500 font-medium">Pending</span></div>
        </div>
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3.5 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2.5 transition-all animate-slide-in ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
        }`}>
          {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
          {toast.msg}
        </div>
      )}

      {/* ═══ Greeting Header ═══ */}
      <div className="flex items-start justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, Admin!</h1>
          <p className="text-sm text-gray-500 mt-1">It's {todayStr}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => selectedUserId && loadCRM(selectedUserId)} disabled={loading || !selectedUserId}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 shadow-sm">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ═══ User Selector — Compact ═══ */}
      <div className="relative mb-7">
        <div className="relative max-w-xl">
          <button
            onClick={() => { setDropdownOpen(!dropdownOpen); setTimeout(() => searchInputRef.current?.focus(), 50); }}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm hover:border-indigo-300 transition-all shadow-sm"
          >
            {selectedUser ? (
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}>
                  {selectedUser.username.charAt(0).toUpperCase()}
                </span>
                <div className="text-left">
                  <span className="font-semibold text-gray-800 block">{selectedUser.username}</span>
                  <span className="text-xs text-gray-400">{selectedUser.full_name || selectedUser.email}</span>
                </div>
              </span>
            ) : (
              <span className="flex items-center gap-2 text-gray-400">
                <Search size={16} />
                Select a user to view CRM dashboard... <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">F4</span>
              </span>
            )}
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden">
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by username, name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleCrmSearchKeyDown}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    autoFocus
                  />
                </div>
              </div>
              <div ref={crmDropdownRef} className="max-h-60 overflow-y-auto">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 size={22} className="animate-spin text-indigo-500" /></div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-400">No users found</div>
                ) : (
                  filteredUsers.map((u, idx) => (
                    <button
                      key={u.id}
                      data-crm-user
                      onClick={() => selectUser(u.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50 transition-colors ${
                        idx === highlightedIdx ? 'bg-indigo-100' : u.id === selectedUserId ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                        u.is_active ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {u.username.charAt(0).toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800 truncate">{u.username}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            u.role === 'master_admin' ? 'bg-purple-100 text-purple-700' :
                            u.role === 'user' ? 'bg-blue-100 text-blue-700' :
                            u.role === 'support' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{u.role}</span>
                        </div>
                        <span className="text-xs text-gray-400 truncate block">{u.full_name || u.email}</span>
                      </div>
                      <span className="text-xs font-mono text-gray-500">{fmtMoney(u.balance)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
            </div>
            <p className="text-sm text-gray-500 font-medium">Loading user data...</p>
          </div>
        </div>
      )}

      {/* No User Selected */}
      {!selectedUserId && !loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-20 h-20 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
              <UsersIcon size={36} className="text-gray-300" />
            </div>
            <p className="text-base font-medium text-gray-400 mb-1">No user selected</p>
            <p className="text-sm text-gray-300">Select a user from the dropdown above to view their CRM dashboard</p>
          </div>
        </div>
      )}

      {/* ═══ CRM Dashboard ═══ */}
      {crmData && !loading && (
        <div className="space-y-6">

          {/* ── Row 1: KPI Stat Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Balance Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }}>
                    <Wallet size={18} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</span>
                </div>
                <button onClick={() => setShowBalanceModal(true)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <MoreHorizontal size={16} className="text-gray-400" />
                </button>
              </div>
              <p className="text-2xl font-bold text-gray-900">{fmtMoney(bal?.balance, bal?.currency)}</p>
              {bal?.total_recharged > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <TrendingUp size={13} className="text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">{fmtMoney(bal.total_recharged, bal.currency)} recharged</span>
                </div>
              )}
            </div>

            {/* Plan Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>
                    <Package size={18} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</span>
                </div>
                <button onClick={() => setShowPlanModal(true)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <MoreHorizontal size={16} className="text-gray-400" />
                </button>
              </div>
              <p className="text-2xl font-bold text-gray-900">{plan?.assigned ? plan.plan_name : 'None'}</p>
              {plan?.assigned && (
                <div className="flex items-center gap-1 mt-2">
                  <span className={`text-xs font-medium ${plan.status === 'ACTIVE' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {plan.status}
                  </span>
                  <span className="text-xs text-gray-400 mx-1">·</span>
                  <span className="text-xs text-gray-500">{fmtNumber(plan.remaining_sms)} SMS left</span>
                </div>
              )}
            </div>

            {/* Rate Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #06b6d4, #22d3ee)' }}>
                    <BadgeDollarSign size={18} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rate</span>
                </div>
                <button onClick={() => setShowRateModal(true)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <MoreHorizontal size={16} className="text-gray-400" />
                </button>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {rates?.assigned && rates.rates?.length > 0 ? fmtRate(rates.rates[0].price_per_sms, rates.rates[0].currency) : '—'}
              </p>
              {rates?.assigned && rates.rates?.length > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-gray-500">per SMS · {rates.rates[0].route_type}</span>
                </div>
              )}
            </div>

            {/* Status Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: p?.is_active ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
                    <Activity size={18} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span>
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{p?.is_active ? 'Active' : 'Inactive'}</p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-gray-500">TPS: {p?.tps_limit || '—'}</span>
                <span className="text-xs text-gray-400 mx-1">·</span>
                <span className="text-xs text-gray-500">{p?.role}</span>
              </div>
            </div>
          </div>

          {/* ── Row 2: Profile + SMS Usage Chart + Connection Status ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

            {/* Profile & Plan Details — Left Column */}
            <div className="lg:col-span-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">User Profile</h3>
                </div>
              </div>
              <div className="p-5">
                {/* User Avatar & Name */}
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold text-white" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                    {p?.username?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900">{p?.full_name || p?.username}</h4>
                    <p className="text-xs text-gray-500">{p?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge active={p?.is_active} />
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        p?.role === 'master_admin' ? 'bg-purple-100 text-purple-700' :
                        p?.role === 'user' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{p?.role}</span>
                    </div>
                  </div>
                </div>
                {/* Info Grid */}
                <div className="space-y-3">
                  {[
                    { icon: Phone, label: 'Mobile', value: p?.mobile },
                    { icon: Building2, label: 'Company', value: p?.company_name },
                    { icon: Calendar, label: 'Created', value: fmtDate(p?.created_at) },
                    { icon: Clock, label: 'Last Login', value: fmtDateTime(p?.last_login) },
                    { icon: Zap, label: 'TPS Limit', value: p?.tps_limit },
                    { icon: Shield, label: 'Balance Enforced', value: p?.balance_enforced ? 'Yes' : 'No' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                        <item.icon size={14} className="text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                        <p className="text-sm text-gray-800 font-medium truncate">{item.value || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* API Key */}
                {p?.api_key && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">API Key</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setShowApiKey(!showApiKey)} className="p-1 text-gray-400 hover:text-indigo-500 transition-colors">
                          {showApiKey ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <button onClick={() => { navigator.clipboard.writeText(p.api_key); showToast('API key copied'); }} className="p-1 text-gray-400 hover:text-indigo-500 transition-colors">
                          <Copy size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs font-mono text-gray-600 mt-1 break-all">{showApiKey ? p.api_key : '••••••••••••••••••••'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* SMS Usage Chart — Center Column */}
            <div className="lg:col-span-5 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">SMS Usage Trend</h3>
                </div>
              </div>
              <div className="p-5">
                <SMSUsageChart data={smsUsage} />
              </div>
            </div>

            {/* Connection & Alerts — Right Column */}
            <div className="lg:col-span-3 space-y-5">
              {/* SMPP Connection Status */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <Activity size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">SMPP Connection</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p?.smpp_is_connected ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      {p?.smpp_is_connected ? <Wifi size={18} className="text-emerald-500" /> : <WifiOff size={18} className="text-red-500" />}
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${p?.smpp_is_connected ? 'text-emerald-700' : 'text-red-700'}`}>
                        {p?.smpp_is_connected ? 'Connected' : 'Disconnected'}
                      </p>
                      <p className="text-[10px] text-gray-400">{p?.smpp_system_id || 'No System ID'}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {p?.smpp_remote_ip && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Remote IP</span>
                        <span className="font-mono text-gray-700">{p.smpp_remote_ip}</span>
                      </div>
                    )}
                    {p?.smpp_bind_type && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Bind Type</span>
                        <span className="font-medium text-gray-700">{p.smpp_bind_type}</span>
                      </div>
                    )}
                    {p?.smpp_connection_count > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Connections</span>
                        <span className="font-medium text-gray-700">{p.smpp_connection_count}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Plan Expiry */}
              {plan?.assigned && plan?.expires_at && (
                <div className={`rounded-xl border p-4 shadow-sm ${
                  new Date(plan.expires_at) < new Date() ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className={new Date(plan.expires_at) < new Date() ? 'text-red-500' : 'text-emerald-500'} />
                    <span className={`text-xs font-bold uppercase ${new Date(plan.expires_at) < new Date() ? 'text-red-600' : 'text-emerald-600'}`}>
                      Plan Expiry
                    </span>
                  </div>
                  <p className={`text-lg font-bold ${new Date(plan.expires_at) < new Date() ? 'text-red-700' : 'text-emerald-700'}`}>
                    {fmtDate(plan.expires_at)}
                  </p>
                </div>
              )}

              {/* Login Alerts Summary */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <Bell size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Login Alerts</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    {alerts?.enabled ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Bell size={11} /> Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                        <BellOff size={11} /> Disabled
                      </span>
                    )}
                    {alerts?.otp_login_enabled && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                        <KeyRound size={11} /> OTP
                      </span>
                    )}
                  </div>
                  {alerts?.emails?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {alerts.emails.slice(0, 2).map((e, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 truncate max-w-[140px]">
                          {e}
                        </span>
                      ))}
                      {alerts.emails.length > 2 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">+{alerts.emails.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 3: All Data Grid ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Plan & Rate Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Plan & Rate</h3>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowPlanModal(true)} className="text-xs px-3 py-1.5 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium">
                    {plan?.assigned ? 'Change Plan' : 'Assign Plan'}
                  </button>
                  <button onClick={() => setShowRateModal(true)} className="text-xs px-3 py-1.5 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium">
                    {rates?.assigned ? 'Update Rate' : 'Assign Rate'}
                  </button>
                </div>
              </div>
              <div className="p-5 space-y-5">
                {/* Plan Details */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Current Plan</h4>
                  {plan?.assigned ? (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-base font-bold text-indigo-700">{plan.plan_name}</span>
                        <StatusBadge active={plan.status === 'ACTIVE'} trueLabel={plan.status} falseLabel={plan.status} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'Channel', value: plan.plan_channel },
                          { label: 'SMS Limit', value: fmtNumber(plan.sms_limit) },
                          { label: 'Remaining', value: fmtNumber(plan.remaining_sms) },
                          { label: 'Daily Limit', value: fmtNumber(plan.daily_limit) },
                          { label: 'Default TPS', value: plan.default_tps },
                          { label: 'Rate/SMS', value: fmtRate(plan.price_per_sms) },
                        ].map((item, i) => (
                          <div key={i} className="bg-white/60 rounded-lg p-2">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                            <p className="text-sm font-semibold text-gray-800">{item.value || '—'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <Package size={32} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No plan assigned</p>
                    </div>
                  )}
                </div>

                {/* Rate Details */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Rate Configuration</h4>
                  {rates?.assigned && rates.rates?.length > 0 ? (
                    <div className="space-y-2">
                      {rates.rates.map((r, i) => (
                        <div key={i} className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-3 border border-cyan-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">{r.route_type}</span>
                            <span className="text-base font-bold text-cyan-700">{fmtRate(r.price_per_sms, r.currency)}<span className="text-xs font-normal text-gray-400">/SMS</span></span>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1">Since {fmtDate(r.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <BadgeDollarSign size={32} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No rate assigned</p>
                    </div>
                  )}
                </div>

                {/* Balance Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-gray-800">Balance Summary</span>
                    <button onClick={() => setShowBalanceModal(true)} className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium">
                      Credit / Debit
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold">Available</p>
                      <p className="text-sm font-bold text-gray-900">{fmtMoney(bal?.balance, bal?.currency)}</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-emerald-500 uppercase font-semibold">Recharged</p>
                      <p className="text-sm font-bold text-emerald-700">{fmtMoney(bal?.total_recharged, bal?.currency)}</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] text-red-500 uppercase font-semibold">Spent</p>
                      <p className="text-sm font-bold text-red-700">{fmtMoney(bal?.total_spent, bal?.currency)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DLT Entities Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Building2 size={16} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">DLT Entities</h3>
              </div>
              <div className="p-5">
                {entities && entities.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity Name</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity ID</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entities.map((entity, i) => (
                          <tr key={entity.id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-3 font-medium text-gray-800 text-sm">{entity.entity_name}</td>
                            <td className="py-2 px-3 font-mono text-gray-600 text-xs">{entity.entity_id || '—'}</td>
                            <td className="py-2 px-3"><StatusBadge active={entity.is_active} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-gray-400 mt-3">{entities.length} entit{entities.length === 1 ? 'y' : 'ies'} mapped</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Building2 size={40} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No DLT entities mapped to this user</p>
                  </div>
                )}
              </div>
            </div>

            {/* IP Whitelist Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Shield size={16} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">IP Whitelist</h3>
              </div>
              <div className="p-5">
                {ipWhitelist ? (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Check size={11} /> {ipWhitelist.approved_count || 0} Approved
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        <Clock size={11} /> {ipWhitelist.pending_count || 0} Pending
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                        <X size={11} /> {ipWhitelist.rejected_count || 0} Rejected
                      </span>
                    </div>

                    {ipWhitelist.approved?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Approved IPs</p>
                        <div className="space-y-1">
                          {ipWhitelist.approved.map((ip, i) => (
                            <div key={i} className="flex items-center justify-between py-2 px-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
                              <div className="flex items-center gap-2">
                                <Globe size={13} className="text-emerald-500" />
                                <span className="text-sm font-mono text-gray-700">{ip.ip_address}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{ip.ip_type}</span>
                              </div>
                              <StatusBadge active={ip.is_active} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {ipWhitelist.pending?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">Pending Requests</p>
                        <div className="space-y-1">
                          {ipWhitelist.pending.map((r, i) => (
                            <div key={i} className="flex items-center justify-between py-2 px-3 bg-amber-50/50 rounded-lg border border-amber-100">
                              <div className="flex items-center gap-2">
                                <Clock size={13} className="text-amber-500" />
                                <span className="text-sm font-mono text-gray-700">{r.ip_address}</span>
                              </div>
                              <span className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {ipWhitelist.rejected?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2">Rejected</p>
                        <div className="space-y-1">
                          {ipWhitelist.rejected.map((r, i) => (
                            <div key={i} className="flex items-center justify-between py-2 px-3 bg-red-50/50 rounded-lg border border-red-100">
                              <div className="flex items-center gap-2">
                                <X size={13} className="text-red-500" />
                                <span className="text-sm font-mono text-gray-700">{r.ip_address}</span>
                              </div>
                              <span className="text-[10px] text-gray-400">{r.remarks || fmtDate(r.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!ipWhitelist.approved?.length && !ipWhitelist.pending?.length && !ipWhitelist.rejected?.length && (
                      <p className="text-sm text-gray-400 text-center py-8">No IP whitelist records</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">No IP whitelist data</p>
                )}
              </div>
            </div>

            {/* Block Rules Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert size={16} className="text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Block Rules</h3>
                </div>
              </div>
              <div className="p-5 space-y-5">
                {/* Block Keywords */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Block Keywords</h4>
                    <button onClick={() => { setNewKwText(''); setShowAddKwModal(true); }} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200">
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="relative mb-3">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={kwSearch} onChange={e => setKwSearch(e.target.value)} placeholder="Search keywords..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-300" />
                  </div>
                  {blockKeywords && blockKeywords.total > 0 ? (
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs text-gray-500">
                          <strong className="text-gray-700">{blockKeywords.total}</strong> total
                          {blockKeywords.user_count > 0 && <> · <span className="text-purple-600">{blockKeywords.user_count} user</span></>}
                          {blockKeywords.global_count > 0 && <> · <span className="text-blue-600">{blockKeywords.global_count} global</span></>}
                        </span>
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {blockKeywords.keywords
                          .filter(kw => !kwSearch || kw.keyword.toLowerCase().includes(kwSearch.toLowerCase()))
                          .map((kw, i) => (
                          <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                kw.scope === 'USER' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}>{kw.keyword}</span>
                              <span className="text-[9px] text-gray-400">{kw.scope}</span>
                            </div>
                            {kw.scope === 'USER' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditKw(kw); setNewKwText(kw.keyword); }} className="p-1 text-gray-400 hover:text-blue-600"><Edit3 size={12} /></button>
                                <button onClick={() => handleDeleteKeyword(kw.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <ShieldAlert size={24} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No block keywords</p>
                    </div>
                  )}
                </div>

                {/* Block Mobiles */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Blocked Mobiles</h4>
                    <button onClick={() => { setNewMbText(''); setNewMbReason(''); setShowAddMbModal(true); }} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200">
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="relative mb-3">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={mbSearch} onChange={e => setMbSearch(e.target.value)} placeholder="Search mobiles..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300" />
                  </div>
                  {blockMobiles && blockMobiles.total > 0 ? (
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs text-gray-500">
                          <strong className="text-gray-700">{blockMobiles.total}</strong> total
                          {blockMobiles.user_count > 0 && <> · <span className="text-purple-600">{blockMobiles.user_count} user</span></>}
                          {blockMobiles.global_count > 0 && <> · <span className="text-blue-600">{blockMobiles.global_count} global</span></>}
                        </span>
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {blockMobiles.mobiles
                          .filter(m => !mbSearch || m.mobile_number.includes(mbSearch))
                          .map((m, i) => (
                          <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-semibold border ${
                                m.scope === 'USER' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}>{m.mobile_number}</span>
                              {m.reason && <span className="text-[9px] text-gray-400 truncate max-w-[80px]" title={m.reason}>{m.reason}</span>}
                            </div>
                            {m.scope === 'USER' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditMb(m); setNewMbText(m.mobile_number); setNewMbReason(m.reason || ''); }} className="p-1 text-gray-400 hover:text-blue-600"><Edit3 size={12} /></button>
                                <button onClick={() => handleDeleteMobile(m.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <Phone size={24} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No blocked mobile numbers</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* IP History Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Globe size={16} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">IP History</h3>
              </div>
              <div className="p-5">
                {ipHistory && ipHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">IP Address</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Logins</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Trusted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ipHistory.map((ip, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-3 font-mono text-gray-700 text-sm">{ip.ip_address}</td>
                            <td className="py-2 px-3 text-gray-600 text-sm">{ip.location || '—'}</td>
                            <td className="py-2 px-3 text-center font-medium text-gray-700 text-sm">{ip.login_count}</td>
                            <td className="py-2 px-3 text-center">
                              {ip.is_trusted ? <Shield size={13} className="text-emerald-500 mx-auto" /> : <AlertTriangle size={13} className="text-amber-500 mx-auto" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Globe size={40} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No IP history available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Transactions Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <CreditCard size={16} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">Transactions</h3>
              </div>
              <div className="p-5">
                {recharges && recharges.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recharges.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                {r.transaction_type === 'recharge' ? (
                                  <ArrowUpCircle size={16} className="text-emerald-500" />
                                ) : (
                                  <ArrowDownCircle size={16} className="text-red-500" />
                                )}
                                <span className="font-medium text-gray-700 capitalize text-sm">{r.transaction_type}</span>
                              </div>
                            </td>
                            <td className={`py-2 px-3 font-bold text-sm ${r.transaction_type === 'recharge' ? 'text-emerald-600' : 'text-red-600'}`}>
                              {r.transaction_type === 'recharge' ? '+' : '-'}{fmtMoney(r.amount, bal?.currency)}
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-xs">{fmtDate(r.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard size={40} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No recent transactions</p>
                  </div>
                )}
              </div>
            </div>

            {/* Masking Card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Fingerprint size={16} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">Content Masking</h3>
              </div>
              <div className="p-5">
                {crmData?.masking ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    {crmData.masking.masking_enabled ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-violet-50 text-violet-700 border border-violet-200">
                        <Fingerprint size={14} /> Masking Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-50 text-gray-400 border border-gray-200">
                        <Fingerprint size={14} /> Masking Disabled
                      </span>
                    )}
                    {crmData.masking.masking_enabled && crmData.masking.type_names?.length > 0 && (
                      <>
                        {crmData.masking.type_names.map((t, i) => (
                          <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {t}
                          </span>
                        ))}
                        {crmData.masking.template_count > 0 && (
                          <span className="text-xs text-gray-500">{crmData.masking.template_count} template(s) masked</span>
                        )}
                        {crmData.masking.mobile_mask_pattern && crmData.masking.type_names?.includes('Mobile') && (
                          <span className="text-xs text-gray-500">Pattern: {crmData.masking.mobile_mask_pattern}</span>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Fingerprint size={40} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No masking configuration</p>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      )}

      {/* ═══ Modals ═══ */}

      {/* Assign Plan Modal */}
      {showPlanModal && (
        <Modal show={showPlanModal} onClose={() => setShowPlanModal(false)} title="Assign Plan">
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {plans.length > 0 ? (
              plans.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleAssignPlan(p.id)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800">{p.plan_name}</span>
                    <span className="text-sm text-gray-500">{p.plan_channel}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.sms_limit ? `${p.sms_limit} SMS` : 'Unlimited'} · {p.validity_days || 0} days · ₹{p.price_per_sms || 0}/SMS
                  </div>
                </button>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No plans available</p>
            )}
          </div>
        </Modal>
      )}

      {/* Rate Modal */}
      <RateModal
        show={showRateModal}
        onClose={() => setShowRateModal(false)}
        onSubmit={handleAssignRate}
        rates={rates?.rates || []}
        currency={bal?.currency}
      />

      {/* Balance Modal */}
      <BalanceModal
        show={showBalanceModal}
        onClose={() => setShowBalanceModal(false)}
        onSubmit={handleCreditDebit}
        currency={bal?.currency}
      />

      {/* Add/Edit Keyword Modal */}
      <Modal show={showAddKwModal} onClose={() => { setShowAddKwModal(false); setEditKw(null); setNewKwText(''); }} title={editKw ? 'Edit Keyword' : 'Add Block Keyword'}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Keyword</label>
            <input
              type="text"
              value={newKwText}
              onChange={e => setNewKwText(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-300"
              placeholder="Enter keyword"
              autoFocus
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => { setShowAddKwModal(false); setEditKw(null); setNewKwText(''); }} className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              Cancel
            </button>
            <button onClick={editKw ? handleUpdateKeyword : handleAddKeyword} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-lg hover:bg-purple-600 transition-colors">
              {editKw ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Mobile Modal */}
      <Modal show={showAddMbModal} onClose={() => { setShowAddMbModal(false); setEditMb(null); setNewMbText(''); setNewMbReason(''); }} title={editMb ? 'Edit Mobile' : 'Add Blocked Mobile'}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Number</label>
            <input
              type="text"
              value={newMbText}
              onChange={e => setNewMbText(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
              placeholder="Enter mobile number"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              value={newMbReason}
              onChange={e => setNewMbReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300 resize-none"
              placeholder="Why is this number blocked?"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => { setShowAddMbModal(false); setEditMb(null); setNewMbText(''); setNewMbReason(''); }} className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              Cancel
            </button>
            <button onClick={editMb ? handleUpdateMobile : handleAddMobile} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">
              {editMb ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserCRMNew;
